import {FormGroup} from '@angular/forms';

import {
    AccountSubTypeEnum,
    ApiError,
    GroupHardBuyingPowerInterface,
    GroupHardBuyingPowerModel,
    HttpStatusCodesEnum,
    ServerResponseData,
    TradingAccountModel,
    TradingAccountStatusesEnum
} from '@spa/api';

import {BaseControl, Helper} from '../../../shared/classes';

import {
    TradingGroupHardBuyingPowerCurrencyRequiredKey,
    TradingGroupRiskThresholdsOrderKey
} from 'appAssets/descriptors/trading-groups-form.descriptors';

/**
 * Reads a Total Hard BP or a threshold off the form as a number, because the amount widget emits "12,345.60", '' or null
 * and the backend parses only a plain number.
 */
export const TradingGroupHardBuyingPowerValue = (value: any): number => {
    if (value === null || value === undefined || value === '') {
        return null;
    }

    const parsed = Helper.getAmountValue(value);

    return isNaN(parsed) ? null : parsed;
};

/** A Total Hard BP needs a currency, and the rule spans two controls, so it cannot live on either one's validators. */
export const TradingGroupHardBuyingPowerCurrencyValid = (
    formGroup: FormGroup,
    amountControl: BaseControl,
    currencyControl: BaseControl,
    messagesFunction: (controlLabel?: string, controlLength?: number) => { [key: string]: string }
): boolean => {
    currencyControl.resetErrors();

    const values = formGroup.getRawValue();

    if (TradingGroupHardBuyingPowerValue(values[amountControl.name]) === null || values[currencyControl.name]) {
        return true;
    }

    currencyControl.setError(messagesFunction()[TradingGroupHardBuyingPowerCurrencyRequiredKey]);

    return false;
};

/** What the hard buying-power screens show for an empty ceiling or threshold, the backend's own word. */
export const GroupHardBuyingPowerNotSet = 'Not set';

/** The model from a GET or PUT answer, or null when it failed or came back Ok with no data, which is how a 404 with a body arrives. */
export const HardBuyingPowerFromResponse = <I, M>(
    response: ServerResponseData<I>, model: new (data: I) => M
): M | null => response?.status === HttpStatusCodesEnum.Ok && response.data ? new model(response.data as I) : null;

/** The group model from a GET or PUT answer, or null, which the two group cards share. */
export const GroupHardBuyingPowerFromResponse = (
    response: ServerResponseData<GroupHardBuyingPowerInterface>
): GroupHardBuyingPowerModel | null => HardBuyingPowerFromResponse(response, GroupHardBuyingPowerModel);

/** A 400's backend reason verbatim and the fallback for anything else, since no other status carries a reason meant for operators. */
export const GroupHardBuyingPowerErrorMessage = (response: ServerResponseData<any>, fallback: string): string => {
    const errors = response?.data as ApiError[];
    const error = Array.isArray(errors) ? errors[0] : null;

    return error?.status === HttpStatusCodesEnum.BadRequest && typeof error.data === 'string' && error.data ? error.data : fallback;
};

/** What spa gives as the text of a 400 the backend sent without a reason, ASP.NET's default ProblemDetails title. */
const BareBadRequestTitle = 'Bad Request';

/** A bare backend error code, such as 1007 for an account whose status refuses the change, which is no reason for operators. */
const BackendErrorCodePattern = /^\d+$/;

/** Why an account create or update failed: a 400's backend reason verbatim, or the page's own text when the backend gave none. */
export const AccountSaveErrorMessage = (response: ServerResponseData<any>, fallback: string): string => {
    const reason = GroupHardBuyingPowerErrorMessage(response, null);

    return reason && reason !== BareBadRequestTitle && !BackendErrorCodePattern.test(reason) ? reason : fallback;
};

/** Two fixed decimals with thousands separators and no currency, as every hard buying-power amount shows. */
export const HardBuyingPowerFormatAmount = (amount: number): string => Helper.formatDecimal(amount, null, 2, 2, false);

/** An amount as the hard buying-power screens show it, "5,000.00 USD" or Not set. */
export const HardBuyingPowerAmountDisplay = (amount: number | null | undefined, currency: string | null): string =>
    amount === null || amount === undefined ? GroupHardBuyingPowerNotSet : `${HardBuyingPowerFormatAmount(amount)} ${currency}`;

/** The ceiling as both group tabs show it, "5,000.00 USD" or Not set. */
export const GroupHardBuyingPowerCeilingDisplay = (hardBuyingPower: GroupHardBuyingPowerModel): string =>
    HardBuyingPowerAmountDisplay(hardBuyingPower?.totalHardBuyingPower, hardBuyingPower?.totalHardBuyingPowerCurrency);

/** Close-cancel must sit strictly below liquidation (S3), a rule across two controls, so it cannot live on either one's validators. */
export const TradingGroupRiskThresholdsOrderValid = (
    formGroup: FormGroup,
    liquidationControl: BaseControl,
    closeCancelControl: BaseControl,
    messagesFunction: (controlLabel?: string, controlLength?: number) => { [key: string]: string }
): boolean => {
    closeCancelControl.resetErrors();

    const values = formGroup.getRawValue();
    const liquidation = TradingGroupHardBuyingPowerValue(values[liquidationControl.name]);
    const closeCancel = TradingGroupHardBuyingPowerValue(values[closeCancelControl.name]);

    if (liquidation === null || closeCancel === null || closeCancel < liquidation) {
        return true;
    }

    closeCancelControl.setError(messagesFunction()[TradingGroupRiskThresholdsOrderKey]);

    return false;
};

/** The distinct currencies of a group's live Hard BP accounts, which S19 makes the next one's while the group has no Total Hard BP. */
export const HardBuyingPowerLiveCurrencies = (accounts: TradingAccountModel[]): string[] => (accounts || [])
    .filter((account: TradingAccountModel) => account.subType === AccountSubTypeEnum.TradingHardBp && !!account.currencyCode
        && [TradingAccountStatusesEnum.Closed, TradingAccountStatusesEnum.Rejected].indexOf(account.status) < 0)
    .map((account: TradingAccountModel) => account.currencyCode.toUpperCase())
    .filter((currency: string, index: number, currencies: string[]) => currencies.indexOf(currency) === index);
